import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonProgressBar,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave
} from '@ionic/react';
import TitleBar from '../components/TitleBar.tsx';
import {
  analyticsOutline, briefcaseOutline,
  businessOutline,
  calendarOutline,
  cashOutline,
  chevronDownOutline,
  chevronUpOutline,
  close,
  closeOutline,
  funnel,
  locationOutline,
  refreshOutline,
  searchOutline,
  timeOutline,
  checkmarkCircleOutline,
  sparklesOutline,
  heart,
  heartOutline,
  bookmarkOutline,
  documentTextOutline,
  linkOutline
} from 'ionicons/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import CreativeLoader from '../components/CreativeLoader.tsx';
import JobCard from '../components/JobCard.tsx';
import FitAnalysisModal from '../components/FitAnalysisModal.tsx';
import CoverLetterModal from '../components/CoverLetterModal.tsx';
import SelectOptionRow from '../components/SelectOptionRow.tsx';
import ActiveSearchWidget from '../components/ActiveSearchWidget.tsx';
import useJobs from '../hooks/useJobs.ts';
import { JobFilters } from '../services/JobsService.ts';
import { jobs as jobsApi } from '../services/api.ts';
import { useAuth } from '../context/AuthContext.tsx';
import './Jobs.css';

const Jobs: React.FC = () => {
  // Jobs service hook
  const {
    allJobs,
    jobs,
    displayedJobs,
    searchStats,
    userSearches,
    loading,
    initialLoading,
    searchesLoading,
    searchInProgress,
    searchProgress,
    hasMoreJobs,
    loadSearchResults,
    loadUserSearches,
    updateFilters,
    loadMoreJobs,
    clearResults,
    revealUntilIndex,
    initiateNewSearch,
    dismissActiveSearch,
    activeSearches
  } = useJobs();

  // Component state
  const searchInputRef = useRef<HTMLIonInputElement>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedSearchId, setSelectedSearchId] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isInitialSetupComplete, setIsInitialSetupComplete] = useState(false);
  const [isMounting, setIsMounting] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchHistoryModal, setShowSearchHistoryModal] = useState(false);
  const [showOnlyWithSalary, setShowOnlyWithSalary] = useState(false);
  const [showFitAnalysisModal, setShowFitAnalysisModal] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [modalJobId, setModalJobId] = useState<string | null>(null);
  const [modalJobTitle, setModalJobTitle] = useState<string | undefined>(undefined);
  const [hasUserClearedSearch, setHasUserClearedSearch] = useState(false);

  // Favorites state
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('job_favorites');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const toggleFavorite = useCallback((jobId: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      localStorage.setItem('job_favorites', JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  const [filters, setFilters] = useState<JobFilters>({
    location: '',
    jobType: '',
    salary: '',
    datePosted: ''
  });

  // Auth context
  const { user } = useAuth();

  // New state for unified page
  const [inputTab, setInputTab] = useState<'url' | 'description'>('url');
  const [jobListTab, setJobListTab] = useState<'scraped' | 'saved'>(() => {
    // Restore last active tab from session storage
    const saved = sessionStorage.getItem('jobs:activeTab');
    // Only allow 'scraped' or 'saved' tabs (searched jobs removed)
    if (saved === 'scraped' || saved === 'saved') {
      return saved;
    }
    return 'scraped';
  });
  const [usePreferences, setUsePreferences] = useState(true); // Toggle for using preferences in search

  // Paste job state
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [pasteJobLoading, setPasteJobLoading] = useState(false);
  const [pasteJobError, setPasteJobError] = useState('');

  // Scraped jobs state
  const [scrapedJobs, setScrapedJobs] = useState<any[]>([]);
  const [scrapedJobsLoading, setScrapedJobsLoading] = useState(false);
  const [scrapedJobsError, setScrapedJobsError] = useState<string | null>(null);

  // Saved jobs state
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [savedJobsLoading, setSavedJobsLoading] = useState(false);
  const [savedJobsError, setSavedJobsError] = useState<string | null>(null);

  // Routing and responsive
  const history = useHistory();
  const locationRouter = useLocation();
  const isMobile = useMemo(() => typeof window !== 'undefined' && window.innerWidth < 768, []);

  // Refs for scroll restoration and initialization
  const initializationRef = useRef(false);
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const hasAutoSelectedRef = useRef<string | null>(null); // Track which search we auto-selected for

  const getScrollKey = useCallback((id?: string | null) => id ? `jobs:scrollY:${id}` : 'jobs:scrollY', []);
  const getRestoreKey = useCallback((id?: string | null) => id ? `jobs:restore:${id}` : 'jobs:restore', []);
  const getAnchorKey = useCallback((id?: string | null) => id ? `jobs:anchor:${id}` : 'jobs:anchor', []);
  const getSelectedKey = useCallback((id?: string | null) => id ? `jobs:selected:${id}` : 'jobs:selected', []);

  // Helper to get scroll context key based on active tab
  const getContextKey = useCallback(() => {
    if (jobListTab === 'scraped') return 'scraped';
    if (jobListTab === 'saved') return 'saved';
    return selectedSearchId || 'searched';
  }, [jobListTab, selectedSearchId]);

  const wasDataLoadedThisSession = sessionStorage.getItem('jobs_data_loaded') === 'true';

  // Client-side filters (Salary & Favorites)
  const filteredJobsToDisplay = useMemo(() => {
    let result = displayedJobs;

    if (showOnlyWithSalary) {
      result = result.filter((job: any) => job.has_salary === true);
    }

    if (showFavoritesOnly) {
      result = result.filter((job: any) => favorites.has(job.id));
    }

    return result;
  }, [displayedJobs, showOnlyWithSalary, showFavoritesOnly, favorites]);

  // Load user searches and auto-select if available
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await loadUserSearches();
        setIsInitialSetupComplete(true);
        // Mark as loaded in sessionStorage
        sessionStorage.setItem('jobs_data_loaded', 'true');
      } catch (error) {
        console.error('Failed to load initial data:', error);
        setIsInitialSetupComplete(true);
      } finally {
        setIsMounting(false);
      }
    };

    if (!initializationRef.current) {
      initializationRef.current = true;
      loadInitialData();
      loadScrapedJobs(); // Also load scraped jobs on mount
    }
  }, [loadUserSearches]);

  // Load scraped jobs
  const loadScrapedJobs = async () => {
    try {
      setScrapedJobsLoading(true);
      setScrapedJobsError(null);
      const response = await jobsApi.getJobHistory(1, 100); // Get more jobs for the list
      // Map created_at to date_posted for JobCard compatibility
      const mappedJobs = response.map((job: any) => ({
        ...job,
        date_posted: job.created_at
      }));
      setScrapedJobs(mappedJobs);
    } catch (err: any) {
      console.error('Error loading scraped jobs:', err);
      setScrapedJobsError(err.response?.data?.detail || 'Failed to load scraped jobs');
    } finally {
      setScrapedJobsLoading(false);
    }
  };

  // Load saved jobs
  const loadSavedJobs = async () => {
    try {
      setSavedJobsLoading(true);
      setSavedJobsError(null);
      const response = await jobsApi.getSavedJobs();
      // Flatten the nested structure: extract job data from each item
      const flattenedJobs = response.map((item: any) => ({
        ...item.job,
        activity_id: item.activity_id,
        saved_at: item.saved_at,
        notes: item.notes,
        date_posted: item.job.created_at || item.saved_at  // Map created_at to date_posted
      }));
      setSavedJobs(flattenedJobs);
    } catch (err: any) {
      console.error('Error loading saved jobs:', err);
      setSavedJobsError(err.response?.data?.detail || 'Failed to load saved jobs');
    } finally {
      setSavedJobsLoading(false);
    }
  };

  // Reload scraped jobs when switching to scraped tab
  useEffect(() => {
    // Persist active tab to session storage
    sessionStorage.setItem('jobs:activeTab', jobListTab);

    if (jobListTab === 'scraped' && scrapedJobs.length === 0) {
      loadScrapedJobs();
    }
  }, [jobListTab]);

  // Reload saved jobs when switching to saved tab
  useEffect(() => {
    if (jobListTab === 'saved' && savedJobs.length === 0) {
      loadSavedJobs();
    }
  }, [jobListTab]);

  // Handle paste job submission (URL or description)
  const handlePasteJobSubmit = async () => {
    if (inputTab === 'url') {
      if (!jobUrl.trim()) {
        setPasteJobError('Please enter a job URL');
        return;
      }
      // Validate URL format
      try {
        new URL(jobUrl.trim());
      } catch {
        setPasteJobError('Please enter a valid URL');
        return;
      }
    } else if (inputTab === 'description') {
      if (!jobDescription.trim()) {
        setPasteJobError('Please paste a job description');
        return;
      }
    }

    setPasteJobError('');
    setPasteJobLoading(true);

    try {
      let response;
      if (inputTab === 'url') {
        response = await jobsApi.scrapeJob(jobUrl.trim(), (user as any)?.default_resume_id);
      } else {
        response = await jobsApi.createJobFromText(jobDescription.trim(), (user as any)?.default_resume_id);
      }

      const scrapedJobId = response.scraped_job_id;

      // Clear inputs
      setJobUrl('');
      setJobDescription('');

      // Reload scraped jobs list
      await loadScrapedJobs();

      // Switch to scraped jobs tab
      setJobListTab('scraped');

      // Navigate to the job detail page
      history.push(`/app/jobs/scraped/${scrapedJobId}`);

    } catch (err: any) {
      console.error('Error submitting job:', err);

      if (err.response?.status === 400) {
        setPasteJobError(err.response.data?.detail || 'Invalid job data');
      } else if (err.response?.status === 402) {
        setPasteJobError('Insufficient credits. Please upgrade your plan.');
      } else if (err.response?.status === 429) {
        setPasteJobError('Too many requests. Please try again later.');
      } else {
        setPasteJobError(err.response?.data?.detail || 'Failed to process job. Please try again.');
      }
    } finally {
      setPasteJobLoading(false);
    }
  };

  // Auto-select first search with results if none selected
  useEffect(() => {
    if (isInitialSetupComplete && !selectedSearchId && userSearches.length > 0 && !hasUserClearedSearch) {
      // Find first search with results
      const bestSearch = userSearches.find(s => s.total_jobs > 0) || userSearches[0];
      if (bestSearch) {
        console.log('✨ Auto-selecting best search:', bestSearch.user_search_query_id);
        setSelectedSearchId(bestSearch.user_search_query_id);
      }
    }
  }, [isInitialSetupComplete, selectedSearchId, userSearches, hasUserClearedSearch]);

  // Navigation lifecycle - Jobs page coming into view
  // Note: We no longer auto-refresh on page enter - use manual refresh button instead
  useIonViewWillEnter(() => {
    console.log('👁️ Jobs page coming into view (data preserved from cache)');
  });

  // Load jobs when search selection changes
  // Filters are applied client-side via updateFilters, so we always load with empty filters
  useEffect(() => {
    if (!selectedSearchId || !isInitialSetupComplete) return;

    const performSearch = async () => {
      await loadSearchResults({
        searchId: selectedSearchId,
        filters: { location: '', jobType: '', salary: '', datePosted: '' }
      });
    };

    performSearch();
  }, [selectedSearchId, isInitialSetupComplete, loadSearchResults]);

  // Auto-select first job ONLY when loading a new search (not on filter changes)
  // Mental model: Job detail has NOTHING to do with filters - selected job stays selected
  useEffect(() => {
    if (!selectedSearchId || loading || initialLoading) {
      return;
    }

    // Only auto-select ONCE per search (not on every filter change)
    if (hasAutoSelectedRef.current !== selectedSearchId && allJobs.length > 0) {
      console.log('🔄 Auto-selecting first job for new search:', allJobs[0].id);
      setSelectedJobId(allJobs[0].id);
      hasAutoSelectedRef.current = selectedSearchId; // Mark this search as handled
    }
  }, [selectedSearchId, loading, initialLoading, allJobs]);

  // Debounce timer ref for filter changes
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isApplyingFiltersRef = useRef(false);
  const isProgrammaticFilterChangeRef = useRef(false);

  // Cleanup filter timeout on unmount
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  // Handle filter changes with debouncing to prevent rapid-fire updates
  const handleFilterChange = useCallback((filterType: keyof JobFilters, value: string) => {
    console.log(`🔔 handleFilterChange CALLED: ${filterType}=${value}, programmatic=${isProgrammaticFilterChangeRef.current}, applying=${isApplyingFiltersRef.current}`);

    // Ignore programmatic changes (when we update state ourselves)
    if (isProgrammaticFilterChangeRef.current) {
      console.log('⏸️ Ignoring programmatic filter change');
      return;
    }

    // Prevent concurrent filter applications
    if (isApplyingFiltersRef.current) {
      console.log('⏸️ Filter application in progress, ignoring duplicate call');
      return;
    }

    // Update UI immediately for responsiveness (mark as programmatic to avoid retrigger)
    isProgrammaticFilterChangeRef.current = true;
    setFilters(prev => {
      const updated = { ...prev, [filterType]: value };
      // Reset flag after enough time for IonSelect to process the change
      setTimeout(() => {
        console.log('✅ Resetting programmatic flag (immediate update)');
        isProgrammaticFilterChangeRef.current = false;
      }, 50); // 50ms should be enough for IonSelect onChange to fire and be blocked
      return updated;
    });

    // Clear any pending filter application
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Debounce the actual filter application by 300ms
    filterTimeoutRef.current = setTimeout(() => {
      isApplyingFiltersRef.current = true;

      // Mark as programmatic to avoid retriggering onChange
      isProgrammaticFilterChangeRef.current = true;
      setFilters(currentFilters => {
        const newFilters = { ...currentFilters, [filterType]: value };
        console.log('🔍 Applying filters:', newFilters);
        updateFilters(newFilters);
        // Reset programmatic flag after enough time for IonSelect to process the change
        setTimeout(() => {
          console.log('✅ Resetting programmatic flag (debounced update)');
          isProgrammaticFilterChangeRef.current = false;
        }, 50); // 50ms should be enough for IonSelect onChange to fire and be blocked
        return newFilters;
      });

      // Don't clear selected job - auto-select will handle it if job is filtered out

      // Reset application flag after state updates complete
      setTimeout(() => {
        isApplyingFiltersRef.current = false;
      }, 100);
    }, 300);
  }, [updateFilters]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    // Clear any pending filter application
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    const emptyFilters: JobFilters = {
      location: '',
      jobType: '',
      salary: '',
      datePosted: ''
    };

    setFilters(emptyFilters);
    updateFilters(emptyFilters);
    setShowOnlyWithSalary(false); // Reset salary disclosure filter
  }, [updateFilters]);

  // Handle manual search (NEW FEATURE - separate from resume auto-search)
  const handleManualSearch = useCallback(async () => {
    const query = searchQuery.trim();

    if (query.length < 3) {
      return;
    }

    console.log(`🔍 User initiated manual search: "${query}"`);

    // Reset the "user cleared" flag so we can auto-select the new results
    setHasUserClearedSearch(false);
    setSelectedJobId(null);

    // Initiate the search (this will handle polling and auto-loading results)
    await initiateNewSearch(query, usePreferences);

    // Clear the search input after initiating
    setSearchQuery('');
  }, [searchQuery, usePreferences, initiateNewSearch]);

  // Handle search change
  const handleSearchChange = useCallback((nextSearchId: string) => {
    // Save current scroll + anchor for the existing search before switching
    (async () => {
      try {
        if (selectedSearchId) {
          const el = await contentRef.current?.getScrollElement();
          if (el) {
            sessionStorage.setItem(getScrollKey(selectedSearchId), String(el.scrollTop));
            // find the first fully visible job card as anchor
            const list = el.querySelector('ion-list');
            const cards = list ? (list.querySelectorAll('ion-card') as NodeListOf<HTMLElement>) : null;
            if (cards && cards.length) {
              const scrollTop = el.scrollTop;
              const anchorCard = Array.from(cards).find(c => c.offsetTop + c.offsetHeight > scrollTop);
              if (anchorCard?.id) {
                sessionStorage.setItem(getAnchorKey(selectedSearchId), anchorCard.id);
              }
            }
          }
        }
      } catch { }
    })();

    setSelectedSearchId(nextSearchId);
    setSelectedJobId(null);
    clearAllFilters();
    // If the next search has a saved position, mark it for restoration
    const savedForNext = sessionStorage.getItem(getScrollKey(nextSearchId));
    if (savedForNext) {
      sessionStorage.setItem(getRestoreKey(nextSearchId), '1');
    }
    // Clear jobId from URL when switching searches
    if (!isMobile) {
      const params = new URLSearchParams(locationRouter.search);
      params.delete('jobId');
      const query = params.toString();
      history.replace(`/app/jobs${query ? `?${query}` : ''}`);
    }
  }, [clearAllFilters, history, isMobile, locationRouter.search, getScrollKey, getRestoreKey, selectedSearchId]);
  const handleJobClick = useCallback((jobId: string) => {
    if (isMobile) {
      // Save current scroll before navigating to detail
      (async () => {
        try {
          const el = await contentRef.current?.getScrollElement();
          if (el) {
            const scrollKey = getScrollKey(selectedSearchId);
            const restoreKey = getRestoreKey(selectedSearchId);
            sessionStorage.setItem(scrollKey, String(el.scrollTop));
            sessionStorage.setItem(restoreKey, '1');
            if (selectedSearchId) {
              sessionStorage.setItem(getSelectedKey(selectedSearchId), jobId);
            }
          }
        } catch { }
      })();
      history.push(`/app/jobs/${jobId}`);
    } else {
      setSelectedJobId(jobId);
      const params = new URLSearchParams(locationRouter.search);
      params.set('jobId', jobId);
      history.replace(`/app/jobs?${params.toString()}`);
    }
  }, [history, isMobile, locationRouter.search, getScrollKey, getRestoreKey, getSelectedKey, selectedSearchId]);

  // Handle scraped job click (uses different route /app/jobs/scraped/:id)
  const handleScrapedJobClick = useCallback(async (jobId: string) => {
    // Save scroll position before navigating
    try {
      const contextKey = getContextKey();
      console.log('🔵 [SCRAPED] Saving scroll for context:', contextKey);
      const el = await contentRef.current?.getScrollElement();
      if (el) {
        const scrollY = el.scrollTop;
        console.log('🔵 [SCRAPED] Current scroll position:', scrollY);
        sessionStorage.setItem(getScrollKey(contextKey), String(scrollY));
        sessionStorage.setItem(getRestoreKey(contextKey), '1');
        console.log('🔵 [SCRAPED] Saved to sessionStorage:', {
          scrollKey: getScrollKey(contextKey),
          restoreKey: getRestoreKey(contextKey),
          value: scrollY
        });
      } else {
        console.warn('🔵 [SCRAPED] Could not get scroll element');
      }
    } catch (err) {
      console.error('🔵 [SCRAPED] Error saving scroll:', err);
    }
    // Navigate to scraped job detail AFTER scroll is saved
    console.log('🔵 [SCRAPED] Navigating to:', `/app/jobs/scraped/${jobId}`);
    history.push(`/app/jobs/scraped/${jobId}`);
  }, [history, getContextKey, getScrollKey, getRestoreKey]);


  // Sync selected job with URL (desktop deep-link support)
  useEffect(() => {
    if (isMobile) return;
    const params = new URLSearchParams(locationRouter.search);
    const idFromUrl = params.get('jobId');
    let idFromPath: string | null = null;
    if (typeof window !== 'undefined') {
      const m = window.location.pathname.match(/\/app\/jobs\/(.+)$/);
      idFromPath = m && m[1] ? m[1] : null;
    }
    const resolvedId = idFromUrl || idFromPath || null;
    if (resolvedId && resolvedId !== selectedJobId) {
      setSelectedJobId(resolvedId);
      // Normalize URL to query param form for split view
      const search = new URLSearchParams(locationRouter.search);
      search.set('jobId', resolvedId);
      history.replace(`/app/jobs?${search.toString()}`);
    }
  }, [locationRouter.search, isMobile, history, selectedJobId]);

  // Preserve scroll position when leaving Jobs
  useIonViewWillLeave(() => {
    (async () => {
      try {
        const el = await contentRef.current?.getScrollElement();
        if (el) {
          // Save scroll for the current active tab
          const contextKey = jobListTab === 'scraped' ? 'scraped' :
            jobListTab === 'saved' ? 'saved' :
              selectedSearchId || 'searched';

          const scrollKey = getScrollKey(contextKey);
          const restoreKey = getRestoreKey(contextKey);
          console.log('🔵 [SAVE] Saving scroll on leave - tab:', jobListTab, 'context:', contextKey, 'scroll:', el.scrollTop);
          sessionStorage.setItem(scrollKey, String(el.scrollTop));
          sessionStorage.setItem(restoreKey, '1');
          sessionStorage.setItem('jobs:activeTab', jobListTab);
        }
      } catch { }
    })();
  }, [jobListTab, selectedSearchId, getScrollKey, getRestoreKey]);

  // Restore scroll position when re-entering Jobs
  useIonViewDidEnter(() => {
    // Get the persisted tab FIRST (before state updates)
    const persistedTab = sessionStorage.getItem('jobs:activeTab') || 'searched';
    const contextKey = persistedTab === 'scraped' ? 'scraped' :
      persistedTab === 'saved' ? 'saved' :
        selectedSearchId || 'searched';

    const saved = sessionStorage.getItem(getScrollKey(contextKey));
    console.log('🟢 [RESTORE] useIonViewDidEnter - persistedTab:', persistedTab, 'context:', contextKey, 'saved:', saved);
    if (saved) {
      // Mark and attempt immediate restore in case loading is already complete
      sessionStorage.setItem(getRestoreKey(contextKey), '1');
      (async () => {
        try {
          // Wait for scroll element to be ready (retry with longer delays and more retries)
          let el = await contentRef.current?.getScrollElement();
          let retryCount = 0;
          const maxRetries = 15; // Increased from 5 to 15
          const retryDelay = 100; // Increased from 50ms to 100ms

          while (!el && retryCount < maxRetries) {
            console.log('🟢 [RESTORE] Waiting for scroll element, retry:', retryCount);
            await new Promise(r => setTimeout(r, retryDelay));
            el = await contentRef.current?.getScrollElement();
            retryCount++;
          }

          if (!el) {
            console.warn('🟢 [RESTORE] No scroll element found after retries');
            return;
          }

          console.log('🟢 [RESTORE] Scroll element ready! Current tab:', jobListTab, 'Persisted tab:', persistedTab);

          // For scraped/saved tabs, wait for data to load, then restore
          if (persistedTab === 'scraped' || persistedTab === 'saved') {
            const y = parseInt(saved, 10);
            console.log('🟢 [RESTORE] Restoring scraped/saved scroll to:', y);

            // Wait for the specific tab's loading to complete
            let loadingCheckCount = 0;
            while ((persistedTab === 'scraped' && scrapedJobsLoading) ||
              (persistedTab === 'saved' && savedJobsLoading)) {
              if (loadingCheckCount > 30) { // Max 3 seconds
                console.warn('🟢 [RESTORE] Timeout waiting for data to load');
                break;
              }
              console.log('🟢 [RESTORE] Waiting for data to load, count:', loadingCheckCount);
              await new Promise(r => setTimeout(r, 100));
              loadingCheckCount++;
            }

            // Additional wait for rendering
            await new Promise(r => setTimeout(r, 200));

            // Multiple attempts to ensure scroll sticks
            requestAnimationFrame(() => {
              contentRef.current?.scrollToPoint(0, y, 0);
            });
            setTimeout(() => {
              contentRef.current?.scrollToPoint(0, y, 0);
              console.log('🟢 [RESTORE] Scroll restored (100ms)');
            }, 100);
            setTimeout(() => {
              contentRef.current?.scrollToPoint(0, y, 0);
              console.log('🟢 [RESTORE] Scroll restored (300ms)');
            }, 300);
            setTimeout(() => {
              contentRef.current?.scrollToPoint(0, y, 0);
              console.log('🟢 [RESTORE] Scroll restored (500ms)');
            }, 500);
            return;
          }

          // For searched jobs, prefer jumping to the previously selected job if available
          const sel = sessionStorage.getItem(getSelectedKey(contextKey));
          if (sel) {
            const anchorId = `job-${sel}`;
            const idx = displayedJobs.findIndex(j => j.id === sel);
            if (idx >= 0) {
              revealUntilIndex(idx + 3);
              await new Promise(r => setTimeout(r, 0));
            }
            const node = document.getElementById(anchorId);
            if (node) {
              node.scrollIntoView({ block: 'start' });
              setSelectedJobId(sel);
              return;
            }
          }
          const y = parseInt(saved, 10);
          requestAnimationFrame(() => {
            contentRef.current?.scrollToPoint(0, y, 0);
            setTimeout(() => contentRef.current?.scrollToPoint(0, y, 0), 0);
          });
        } catch (err) {
          console.error('🟢 [RESTORE] Error:', err);
        }
      })();
    }
  });

  // After loading completes, perform a deferred restore
  useEffect(() => {
    if (initialLoading || searchesLoading) return;
    const contextKey = getContextKey();
    const restoreKey = getRestoreKey(contextKey);
    const scrollKey = getScrollKey(contextKey);
    const anchorKey = getAnchorKey(contextKey);
    const shouldRestore = sessionStorage.getItem(restoreKey) === '1';
    const saved = sessionStorage.getItem(scrollKey);
    if (!shouldRestore) return;

    const doRestore = async () => {
      try {
        const el = await contentRef.current?.getScrollElement();
        if (!el) return;

        // For scraped/saved tabs, just restore the scroll position directly
        if (jobListTab === 'scraped' || jobListTab === 'saved') {
          const y = parseInt(saved || '0', 10);
          requestAnimationFrame(() => {
            contentRef.current?.scrollToPoint(0, y, 0);
            setTimeout(() => {
              contentRef.current?.scrollToPoint(0, y, 0);
            }, 100);
          });
          return;
        }

        // For searched jobs, try to restore to last selected job for this context
        const sel = sessionStorage.getItem(getSelectedKey(contextKey));
        if (sel) {
          const anchorId = `job-${sel}`;
          const idx = displayedJobs.findIndex(j => j.id === sel);
          if (idx >= 0) {
            revealUntilIndex(idx + 3);
            await new Promise(r => setTimeout(r, 0));
          }
          const node = document.getElementById(anchorId);
          if (node) {
            node.scrollIntoView({ block: 'start' });
            setSelectedJobId(sel);
            return;
          }
        }

        // Fallback: use anchor+absolute scroll position
        // If we have an anchor, ensure it's rendered and scroll to it first
        const anchorId = sessionStorage.getItem(anchorKey);
        if (anchorId) {
          const idx = displayedJobs.findIndex(j => `job-${j.id}` === anchorId);
          if (idx >= 0) {
            revealUntilIndex(idx + 3); // render a bit beyond anchor
            await new Promise(r => setTimeout(r, 0));
            const node = document.getElementById(anchorId);
            if (node) {
              node.scrollIntoView({ block: 'start' });
            }
          }
        }
        // two-phase restore to handle layout paint
        const yStr = saved || '0';
        const y = parseInt(yStr, 10);
        requestAnimationFrame(() => {
          contentRef.current?.scrollToPoint(0, y, 0);
          setTimeout(() => {
            contentRef.current?.scrollToPoint(0, y, 0);
          }, 0);
        });
      } finally {
        sessionStorage.removeItem(restoreKey);
      }
    };
    doRestore();
  }, [initialLoading, searchesLoading, jobListTab, getContextKey, getRestoreKey, getScrollKey, getAnchorKey, getSelectedKey, displayedJobs, revealUntilIndex]);

  // Check for searchId in URL query params (from completion popup)
  useEffect(() => {
    const params = new URLSearchParams(locationRouter.search);
    const searchIdFromUrl = params.get('searchId');

    if (searchIdFromUrl && userSearches.length > 0) {
      // Verify this search ID exists in user's searches
      const searchExists = userSearches.some(s => s.user_search_query_id === searchIdFromUrl);
      if (searchExists && selectedSearchId !== searchIdFromUrl) {
        console.log('📍 Auto-selecting search from URL:', searchIdFromUrl);
        setSelectedSearchId(searchIdFromUrl);
        // Clean up URL after selection
        history.replace('/app/jobs');
      }
    }
  }, [locationRouter.search, userSearches, selectedSearchId, history]);

  // Don't auto-select - let user choose which search to view

  // Handle infinite scroll
  const handleInfiniteScroll = useCallback((ev: CustomEvent<void>) => {
    loadMoreJobs();
    (ev.target as HTMLIonInfiniteScrollElement).complete();
  }, [loadMoreJobs]);

  // Extract unique locations from job results (use allJobs to show all options even when filtered)
  const getLocationOptions = () => {
    if (!allJobs.length) return [];

    const locationSet = new Set<string>();
    const specialLocations = new Set<string>();

    allJobs.forEach(job => {
      if (job.is_remote) {
        specialLocations.add('remote');
      }

      if (job.location?.toLowerCase().includes('hybrid')) {
        specialLocations.add('hybrid');
      }

      if (job.location && job.location.trim()) {
        // Extract city/region from location string
        const location = job.location.trim();
        // Handle formats like "Toronto, Ontario, Canada" -> extract main city
        const mainLocation = location.split(',')[0].trim();
        if (mainLocation && !mainLocation.toLowerCase().includes('remote')) {
          locationSet.add(mainLocation.toLowerCase().replace(/\s+/g, '-'));
        }
      }
    });

    // Combine special locations (remote, hybrid) with regular locations
    const specialOptions = Array.from(specialLocations).sort();
    const regularOptions = Array.from(locationSet).sort();

    return [...specialOptions, ...regularOptions];
  };

  const locationOptions = getLocationOptions();

  // Extract job type options dynamically from jobs (use allJobs to show all options even when filtered)
  const getJobTypeOptions = () => {
    if (!allJobs.length) return [] as string[];
    const set = new Set<string>();
    allJobs.forEach(j => {
      const raw = (j.job_type || '').toLowerCase();
      const key = raw.replace(/[^a-z0-9]/g, '');
      if (!key) return;
      if (key.includes('permanent') || key.includes('regular') || key.includes('fulltime') || key === 'ft' || key.includes('fullt')) set.add('full-time');
      else if (key.includes('parttime') || key === 'pt' || key.includes('partt')) set.add('part-time');
      else if (key.includes('intern') || key.includes('coop') || key.includes('co-op')) set.add('internship');
      else if (key.includes('contract') || key.includes('temp') || key.includes('term')) set.add('contract');
      else if (key.includes('freelance') || key.includes('consult') || key.includes('gig')) set.add('freelance');
    });
    return Array.from(set);
  };
  const jobTypeOptions = getJobTypeOptions();

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(filter => filter !== '') || showOnlyWithSalary;

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <TitleBar title="Job Dashboard" />
      </IonHeader>

      <IonContent ref={contentRef} style={{ '--background': 'var(--app-background-color)' }}>
        {/* Background decorative elements */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Witty search screen - always shown during loading */}
          {(isMounting || initialLoading || searchesLoading) && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '70vh'
            }}>
              <CreativeLoader
                size="large"
                message={initialLoading ? 'Finding your perfect opportunities...' : 'Loading your searches...'}
                showJoke={true}
              />
            </div>
          )}

          {(!isMounting && !initialLoading && !searchesLoading) && (
            <IonGrid style={{ padding: '16px', maxWidth: '1000px', margin: '0 auto' }}>
              {/* Search Section with Input Tabs */}
              <IonRow style={{ marginBottom: '24px' }}>
                <IonCol size="12">
                  <div className="glass-card" style={{ padding: '20px', overflow: 'visible' }}>
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <IonLabel className="heading-md">
                        Add a Job
                      </IonLabel>
                    </div>

                    {/* Input Method Tabs */}
                    <IonSegment
                      value={inputTab}
                      onIonChange={(e) => setInputTab(e.detail.value as 'url' | 'description')}
                      style={{ marginBottom: '16px' }}
                    >
                      <IonSegmentButton value="url" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <IonIcon icon={linkOutline} style={{ marginBottom: '4px' }} />
                        <IonLabel style={{ margin: 0 }}>URL</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="description" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <IonIcon icon={documentTextOutline} style={{ marginBottom: '4px' }} />
                        <IonLabel style={{ margin: 0 }}>Paste</IonLabel>
                      </IonSegmentButton>
                    </IonSegment>

                    {/* Paste URL Input (shown when inputTab === 'url') */}
                    {inputTab === 'url' && (
                      <div>
                        <IonTextarea
                          value={jobUrl}
                          onIonInput={(e) => {
                            setJobUrl(e.detail.value || '');
                            setPasteJobError('');
                          }}
                          placeholder="https://example.com/jobs/view/12345..."
                          rows={3}
                          disabled={pasteJobLoading}
                          style={{
                            '--border-color': pasteJobError ? 'var(--ion-color-danger)' : 'var(--ion-color-light-shade)',
                            '--border-radius': '12px',
                            '--padding-start': '16px',
                            '--padding-end': '16px',
                            '--padding-top': '14px',
                            '--padding-bottom': '14px',
                            fontSize: '14px',
                            fontFamily: 'monospace',
                            background: pasteJobLoading ? 'var(--ion-color-light)' : 'white',
                            marginBottom: '12px'
                          }}
                        />
                        {pasteJobError && (
                          <IonText color="danger" style={{ fontSize: '13px', display: 'block', marginBottom: '12px' }}>
                            {pasteJobError}
                          </IonText>
                        )}
                        <IonButton
                          expand="block"
                          onClick={handlePasteJobSubmit}
                          disabled={pasteJobLoading || !jobUrl.trim()}
                          style={{
                            '--background': 'var(--ion-color-primary)',
                            '--border-radius': '12px',
                            height: '44px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          {pasteJobLoading ? (
                            <>
                              <IonSpinner name="crescent" style={{ marginRight: '8px', width: '18px', height: '18px' }} />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <IonIcon icon={sparklesOutline} slot="start" />
                              Analyze Job
                            </>
                          )}
                        </IonButton>
                      </div>
                    )}

                    {/* Paste Description Input (shown when inputTab === 'description') */}
                    {inputTab === 'description' && (
                      <div>
                        <IonTextarea
                          value={jobDescription}
                          onIonInput={(e) => {
                            setJobDescription(e.detail.value || '');
                            setPasteJobError('');
                          }}
                          placeholder="Paste the full job description here...&#10;&#10;Include title, company, requirements, responsibilities, qualifications, etc."
                          rows={8}
                          disabled={pasteJobLoading}
                          style={{
                            '--border-color': pasteJobError ? 'var(--ion-color-danger)' : 'var(--ion-color-light-shade)',
                            '--border-radius': '12px',
                            '--padding-start': '16px',
                            '--padding-end': '16px',
                            '--padding-top': '14px',
                            '--padding-bottom': '14px',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            background: pasteJobLoading ? 'var(--ion-color-light)' : 'white',
                            marginBottom: '12px'
                          }}
                        />
                        {pasteJobError && (
                          <IonText color="danger" style={{ fontSize: '13px', display: 'block', marginBottom: '12px' }}>
                            {pasteJobError}
                          </IonText>
                        )}
                        <IonButton
                          expand="block"
                          onClick={handlePasteJobSubmit}
                          disabled={pasteJobLoading || !jobDescription.trim()}
                          style={{
                            '--background': 'var(--ion-color-primary)',
                            '--border-radius': '12px',
                            height: '44px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          {pasteJobLoading ? (
                            <>
                              <IonSpinner name="crescent" style={{ marginRight: '8px', width: '18px', height: '18px' }} />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <IonIcon icon={sparklesOutline} slot="start" />
                              Analyze Job
                            </>
                          )}
                        </IonButton>
                      </div>
                    )}
                  </div>
                </IonCol>
              </IonRow>

              {/* Active Search Widgets */}
              {activeSearches.length > 0 && (
                <IonRow style={{ marginBottom: '16px' }}>
                  <IonCol size="12">
                    {activeSearches.map(search => (
                      <div key={search.tempId} style={{ marginBottom: '12px' }}>
                        <ActiveSearchWidget
                          status={search.status}
                          progressMessage={search.query}
                          isComplete={search.isComplete}
                          totalJobs={search.totalJobs}
                          onViewResults={() => {
                            dismissActiveSearch(search.tempId);
                            const anchor = document.getElementById('jobs-list-top');
                            if (anchor) anchor.scrollIntoView({ behavior: 'smooth' });
                          }}
                          onDismiss={() => dismissActiveSearch(search.tempId)}
                        />
                      </div>
                    ))}
                  </IonCol>
                </IonRow>
              )}

              {/* Filters Section */}
              {selectedSearchId && (
                <IonRow style={{ marginBottom: '24px' }}>
                  <IonCol size="12">
                    <div className="glass-card" style={{ padding: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: showFilters ? '16px' : '0'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'rgba(79, 70, 229, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <IonIcon icon={funnel} style={{ color: 'var(--ion-color-primary)', fontSize: '16px' }} />
                          </div>
                          <IonText style={{ fontSize: '15px', fontWeight: '600', color: 'var(--ion-text-color)' }}>
                            Filters
                          </IonText>
                          {hasActiveFilters && !showFilters && (
                            <span style={{
                              background: 'var(--ion-color-primary)',
                              color: 'white',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              Active
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {hasActiveFilters && showFilters && (
                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={clearAllFilters}
                              style={{ fontSize: '13px', height: '32px', color: 'var(--ion-color-medium)' }}
                            >
                              Clear All
                            </IonButton>
                          )}
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => setShowFilters(!showFilters)}
                            style={{
                              '--background': 'var(--ion-color-light)',
                              '--color': 'var(--ion-text-color)',
                              '--border-radius': '8px',
                              fontSize: '13px',
                              height: '32px',
                              fontWeight: '600'
                            }}
                          >
                            {showFilters ? 'Hide' : 'Show'}
                            <IonIcon icon={showFilters ? chevronUpOutline : chevronDownOutline} slot="end" />
                          </IonButton>
                        </div>
                      </div>

                      {/* Expandable Filters Section */}
                      {showFilters && (
                        <div className="animate-slide-up">
                          <IonGrid style={{ padding: '0' }}>
                            <IonRow>
                              <IonCol size="12" sizeMd="3" style={{ padding: '0 6px 12px' }}>
                                <IonLabel style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '8px' }}>Location</IonLabel>
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '10px', padding: '0 4px' }}>
                                  <IonItem lines="none" style={{ '--background': 'transparent', '--min-height': '44px' }}>
                                    <IonIcon icon={locationOutline} slot="start" style={{ color: 'var(--ion-color-medium)', fontSize: '18px', marginInlineEnd: '8px' }} />
                                    <IonSelect
                                      value={filters.location}
                                      placeholder="All Locations"
                                      interface="popover"
                                      onIonChange={(e) => handleFilterChange('location', e.detail.value)}
                                      style={{ fontSize: '14px', fontWeight: '500' }}
                                    >
                                      <IonSelectOption value="">All Locations</IonSelectOption>
                                      {locationOptions.map(location => {
                                        const displayName = location === 'remote' ? 'Remote' :
                                          location === 'hybrid' ? 'Hybrid' :
                                            location.split('-').map(word =>
                                              word.charAt(0).toUpperCase() + word.slice(1)
                                            ).join(' ');
                                        return (
                                          <IonSelectOption key={location} value={location}>
                                            {displayName}
                                          </IonSelectOption>
                                        );
                                      })}
                                    </IonSelect>
                                  </IonItem>
                                </div>
                              </IonCol>
                              <IonCol size="12" sizeMd="3" style={{ padding: '0 6px 12px' }}>
                                <IonLabel style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '8px' }}>Job Type</IonLabel>
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '10px', padding: '0 4px' }}>
                                  <IonItem lines="none" style={{ '--background': 'transparent', '--min-height': '44px' }}>
                                    <IonIcon icon={businessOutline} slot="start" style={{ color: 'var(--ion-color-medium)', fontSize: '18px', marginInlineEnd: '8px' }} />
                                    <IonSelect
                                      value={filters.jobType}
                                      placeholder="All Types"
                                      interface="popover"
                                      onIonChange={(e) => handleFilterChange('jobType', e.detail.value)}
                                      style={{ fontSize: '14px', fontWeight: '500' }}
                                    >
                                      <IonSelectOption value="">All Types</IonSelectOption>
                                      {jobTypeOptions.map(v => (
                                        <IonSelectOption key={v} value={v}>
                                          {v === 'full-time' ? 'Full-time' : v === 'part-time' ? 'Part-time' : v === 'internship' ? 'Internship' : v === 'contract' ? 'Contract' : v === 'freelance' ? 'Freelance' : v}
                                        </IonSelectOption>
                                      ))}
                                    </IonSelect>
                                  </IonItem>
                                </div>
                              </IonCol>
                              <IonCol size="12" sizeMd="3" style={{ padding: '0 6px 12px' }}>
                                <IonLabel style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '8px' }}>Salary Range</IonLabel>
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '10px', padding: '0 4px' }}>
                                  <IonItem lines="none" style={{ '--background': 'transparent', '--min-height': '44px' }}>
                                    <IonIcon icon={cashOutline} slot="start" style={{ color: 'var(--ion-color-medium)', fontSize: '18px', marginInlineEnd: '8px' }} />
                                    <IonSelect
                                      value={filters.salary}
                                      placeholder="Any Salary"
                                      interface="popover"
                                      onIonChange={(e) => handleFilterChange('salary', e.detail.value)}
                                      style={{ fontSize: '14px', fontWeight: '500' }}
                                    >
                                      <IonSelectOption value="">Any Salary</IonSelectOption>
                                      <IonSelectOption value="50k">$50k+</IonSelectOption>
                                      <IonSelectOption value="75k">$75k+</IonSelectOption>
                                      <IonSelectOption value="100k">$100k+</IonSelectOption>
                                      <IonSelectOption value="150k">$150k+</IonSelectOption>
                                      <IonSelectOption value="200k">$200k+</IonSelectOption>
                                    </IonSelect>
                                  </IonItem>
                                </div>
                              </IonCol>
                              <IonCol size="12" sizeMd="3" style={{ padding: '0 6px 12px' }}>
                                <IonLabel style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '8px' }}>Date Posted</IonLabel>
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '10px', padding: '0 4px' }}>
                                  <IonItem lines="none" style={{ '--background': 'transparent', '--min-height': '44px' }}>
                                    <IonIcon icon={calendarOutline} slot="start" style={{ color: 'var(--ion-color-medium)', fontSize: '18px', marginInlineEnd: '8px' }} />
                                    <IonSelect
                                      value={filters.datePosted}
                                      placeholder="Any Time"
                                      interface="popover"
                                      onIonChange={(e) => handleFilterChange('datePosted', e.detail.value)}
                                      style={{ fontSize: '14px', fontWeight: '500' }}
                                    >
                                      <IonSelectOption value="">Any Time</IonSelectOption>
                                      <IonSelectOption value="1d">Past 24 hours</IonSelectOption>
                                      <IonSelectOption value="3d">Past 3 days</IonSelectOption>
                                      <IonSelectOption value="7d">Past week</IonSelectOption>
                                      <IonSelectOption value="30d">Past month</IonSelectOption>
                                    </IonSelect>
                                  </IonItem>
                                </div>
                              </IonCol>
                            </IonRow>
                            {/* Salary Disclosure Filter Row */}
                            <IonRow style={{ marginTop: '8px', paddingTop: '16px', borderTop: '1px solid var(--ion-color-light-shade)' }}>
                              <IonCol size="12">
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 16px',
                                  backgroundColor: 'rgba(16, 185, 129, 0.05)',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(16, 185, 129, 0.1)'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '10px',
                                      background: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}>
                                      <IonIcon icon={cashOutline} style={{ color: 'var(--ion-color-success)', fontSize: '20px' }} />
                                    </div>
                                    <div>
                                      <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: 'var(--ion-text-color)', display: 'block' }}>
                                        Show only jobs with salary
                                      </IonLabel>
                                      <IonText style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                                        {displayedJobs.filter((job: any) => job.has_salary).length} of {displayedJobs.length} jobs have disclosed salary
                                      </IonText>
                                    </div>
                                  </div>
                                  <IonToggle
                                    checked={showOnlyWithSalary}
                                    onIonChange={(e) => setShowOnlyWithSalary(e.detail.checked)}
                                    style={{ '--handle-width': '20px', '--handle-height': '20px' }}
                                  />
                                </div>
                              </IonCol>

                              {/* Favorites Filter */}
                              <IonCol size="12" sizeMd="6" style={{ padding: '0 6px 12px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 16px',
                                  backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(239, 68, 68, 0.1)'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '10px',
                                      background: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}>
                                      <IonIcon icon={heart} style={{ color: 'var(--ion-color-danger)', fontSize: '20px' }} />
                                    </div>
                                    <div>
                                      <IonLabel style={{ fontSize: '14px', fontWeight: '600', color: 'var(--ion-text-color)', display: 'block' }}>
                                        Show favorites only
                                      </IonLabel>
                                      <IonText style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                                        {displayedJobs.filter((job: any) => favorites.has(job.id)).length} saved jobs
                                      </IonText>
                                    </div>
                                  </div>
                                  <IonToggle
                                    checked={showFavoritesOnly}
                                    onIonChange={(e) => setShowFavoritesOnly(e.detail.checked)}
                                    style={{ '--handle-width': '20px', '--handle-height': '20px', '--checked-background': 'var(--ion-color-danger)' }}
                                  />
                                </div>
                              </IonCol>
                            </IonRow>
                          </IonGrid>
                        </div>
                      )}
                    </div>
                  </IonCol>
                </IonRow>
              )}

              {/* Main Content */}
              <IonRow>
                {/* Jobs List */}
                <IonCol size="12">
                  {/* Job List Tabs */}
                  <IonSegment
                    value={jobListTab}
                    onIonChange={(e) => setJobListTab(e.detail.value as 'scraped' | 'saved')}
                    style={{ marginBottom: '16px' }}
                  >
                    <IonSegmentButton value="scraped" style={isMobile ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '0', padding: '6px 4px 10px 4px' } : {}}>
                      <IonIcon icon={documentTextOutline} style={isMobile ? { fontSize: '18px', marginBottom: '3px' } : {}} />
                      <IonLabel style={isMobile ? { margin: 0, fontSize: '10px', lineHeight: '1.2' } : {}}>
                        {isMobile ? `Jobs${scrapedJobs.length > 0 ? ` (${scrapedJobs.length})` : ''}` : `Jobs ${scrapedJobs.length > 0 ? `(${scrapedJobs.length})` : ''}`}
                      </IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="saved" style={isMobile ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '0', padding: '6px 4px 10px 4px' } : {}}>
                      <IonIcon icon={bookmarkOutline} style={isMobile ? { fontSize: '18px', marginBottom: '3px' } : {}} />
                      <IonLabel style={isMobile ? { margin: 0, fontSize: '10px', lineHeight: '1.2' } : {}}>
                        {isMobile ? `Saved${savedJobs.length > 0 ? ` (${savedJobs.length})` : ''}` : `Saved ${savedJobs.length > 0 ? `(${savedJobs.length})` : ''}`}
                      </IonLabel>
                    </IonSegmentButton>
                  </IonSegment>

                  {/* Loading indicator when switching */}
                  {scrapedJobsLoading && jobListTab === 'scraped' && (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px'
                    }}>
                      <CreativeLoader
                        size="medium"
                        message="Loading jobs..."
                        showJoke={false}
                      />
                    </div>
                  )}

                  {/* Scraped Jobs List */}
                  {jobListTab === 'scraped' && (
                    <>
                      {/* Loading indicator for scraped jobs */}
                      {scrapedJobsLoading && (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px 20px'
                        }}>
                          <IonSpinner name="crescent" style={{ transform: 'scale(1.5)' }} />
                          <p style={{ marginTop: '20px', color: 'var(--ion-color-medium)' }}>Loading scraped jobs...</p>
                        </div>
                      )}

                      {/* Scraped Jobs Error */}
                      {scrapedJobsError && (
                        <div className="glass-card" style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          background: 'rgba(239, 68, 68, 0.05)',
                          border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}>
                          <IonText color="danger">
                            <p style={{ margin: 0 }}>{scrapedJobsError}</p>
                          </IonText>
                        </div>
                      )}

                      {/* Scraped Jobs List */}
                      {!scrapedJobsLoading && !scrapedJobsError && (
                        <>
                          {scrapedJobs.length > 0 && (
                            <div style={{
                              padding: '0 8px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              color: 'var(--ion-color-medium)',
                              fontWeight: '500'
                            }}>
                              <IonIcon icon={briefcaseOutline} style={{ fontSize: '18px' }} />
                              <span>
                                {scrapedJobs.length} {scrapedJobs.length === 1 ? 'job' : 'jobs'} scraped
                              </span>
                            </div>
                          )}
                          <IonList style={{ background: 'transparent', padding: '0' }}>
                            {scrapedJobs.length > 0 ? (
                              scrapedJobs.map((job) => (
                                <JobCard
                                  key={job.id}
                                  job={job}
                                  isActive={selectedJobId === job.id}
                                  onClick={handleScrapedJobClick}
                                  showDetailsButton={isMobile}
                                  detailsUrl={`/app/jobs/scraped/${job.id}`}
                                  onOpenOriginal={(jobId, jobUrl) => {
                                    window.open(jobUrl, '_blank', 'noopener,noreferrer');
                                  }}
                                  onFitAnalysis={(jobId) => {
                                    setModalJobId(jobId);
                                    setModalJobTitle(job.title);
                                    setShowFitAnalysisModal(true);
                                  }}
                                  onCoverLetter={(jobId) => {
                                    setModalJobId(jobId);
                                    setModalJobTitle(job.title);
                                    setShowCoverLetterModal(true);
                                  }}
                                  isFavorite={favorites.has(job.id)}
                                  onToggleFavorite={toggleFavorite}
                                />
                              ))
                            ) : (
                              <div className="glass-card" style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '50%',
                                  background: 'var(--ion-color-light)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '24px'
                                }}>
                                  <IonIcon icon={documentTextOutline} style={{ fontSize: '40px', color: 'var(--ion-color-primary)' }} />
                                </div>
                                <h3 className="heading-md" style={{ marginBottom: '8px' }}>
                                  No scraped jobs yet
                                </h3>
                                <p className="text-body" style={{ marginBottom: '24px', maxWidth: '400px' }}>
                                  Use the "Paste URL" or "Paste Description" tabs above to add jobs and analyze them with AI.
                                </p>
                                <IonButton
                                  fill="solid"
                                  onClick={() => {
                                    contentRef.current?.scrollToTop(500);
                                    setInputTab('url');
                                  }}
                                  style={{ '--border-radius': '12px' }}
                                >
                                  Add Your First Job
                                </IonButton>
                              </div>
                            )}
                          </IonList>
                        </>
                      )}
                    </>
                  )}

                  {/* Saved Jobs List */}
                  {jobListTab === 'saved' && (
                    <>
                      {/* Loading indicator for saved jobs */}
                      {savedJobsLoading && (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px 20px'
                        }}>
                          <IonSpinner name="crescent" style={{ transform: 'scale(1.5)' }} />
                          <p style={{ marginTop: '20px', color: 'var(--ion-color-medium)' }}>Loading saved jobs...</p>
                        </div>
                      )}

                      {/* Saved Jobs Error */}
                      {savedJobsError && (
                        <div className="glass-card" style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          background: 'rgba(239, 68, 68, 0.05)',
                          border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}>
                          <IonText color="danger">
                            <p style={{ margin: 0 }}>{savedJobsError}</p>
                          </IonText>
                        </div>
                      )}

                      {/* Saved Jobs List */}
                      {!savedJobsLoading && !savedJobsError && (
                        <>
                          {savedJobs.length > 0 && (
                            <div style={{
                              padding: '0 8px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              color: 'var(--ion-color-medium)',
                              fontWeight: '500'
                            }}>
                              <IonIcon icon={briefcaseOutline} style={{ fontSize: '18px' }} />
                              <span>
                                {savedJobs.length} {savedJobs.length === 1 ? 'job' : 'jobs'} saved
                              </span>
                            </div>
                          )}
                          <IonList style={{ background: 'transparent', padding: '0' }}>
                            {savedJobs.length > 0 ? (
                              savedJobs.map((job) => (
                                <JobCard
                                  key={job.id}
                                  job={job}
                                  isActive={selectedJobId === job.id}
                                  onClick={handleJobClick}
                                  showDetailsButton={isMobile}
                                  detailsUrl={`/app/jobs/${job.id}`}
                                  onOpenOriginal={(jobId, jobUrl) => {
                                    window.open(jobUrl, '_blank', 'noopener,noreferrer');
                                  }}
                                  onFitAnalysis={(jobId) => {
                                    const job = savedJobs.find(j => j.id === jobId);
                                    setModalJobId(jobId);
                                    setModalJobTitle(job?.title);
                                    setShowFitAnalysisModal(true);
                                  }}
                                  onCoverLetter={(jobId) => {
                                    const job = savedJobs.find(j => j.id === jobId);
                                    setModalJobId(jobId);
                                    setModalJobTitle(job?.title);
                                    setShowCoverLetterModal(true);
                                  }}
                                  isFavorite={favorites.has(job.id)}
                                  onToggleFavorite={toggleFavorite}
                                />
                              ))
                            ) : (
                              <div className="glass-card" style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '50%',
                                  background: 'var(--ion-color-light)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '24px'
                                }}>
                                  <IonIcon icon={bookmarkOutline} style={{ fontSize: '40px', color: 'var(--ion-color-primary)' }} />
                                </div>
                                <h3 className="heading-md" style={{ marginBottom: '8px' }}>
                                  No saved jobs yet
                                </h3>
                                <p className="text-body" style={{ marginBottom: '24px', maxWidth: '400px' }}>
                                  Save jobs you're interested in by clicking the bookmark icon on any job card.
                                </p>
                              </div>
                            )}
                          </IonList>
                        </>
                      )}
                    </>
                  )}

                </IonCol>
              </IonRow>
            </IonGrid>
          )}
        </div>
      </IonContent>

      {/* Search History Modal */}
      <IonModal
        isOpen={showSearchHistoryModal}
        onDidDismiss={() => setShowSearchHistoryModal(false)}
        style={{
          '--border-radius': '24px',
          '--box-shadow': '0 10px 40px rgba(0, 0, 0, 0.2)',
          '--width': '90%',
          '--max-width': '600px',
          '--height': '80vh',
          '--max-height': '700px'
        }}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar style={{ '--background': '#ffffff', '--border-width': '0 0 1px 0', '--border-color': 'var(--ion-color-light-shade)' }}>
            <IonTitle className="heading-md" style={{ fontSize: '18px' }}>
              Search History
            </IonTitle>
            <IonButton
              slot="end"
              fill="clear"
              onClick={() => setShowSearchHistoryModal(false)}
              style={{ '--color': 'var(--ion-color-medium)' }}
            >
              <IonIcon icon={close} style={{ fontSize: '24px' }} />
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': 'var(--app-background-color)' }}>
          <div style={{ padding: '16px' }}>
            {userSearches.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid var(--ion-color-light-shade)'
              }}>
                <IonIcon icon={timeOutline} style={{ fontSize: '64px', color: 'var(--ion-color-light-shade)', marginBottom: '16px' }} />
                <p style={{ color: 'var(--ion-text-color)', fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0' }}>
                  No search history
                </p>
                <p style={{ color: 'var(--ion-color-medium)', fontSize: '14px', margin: 0 }}>
                  Your past searches will appear here
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[...userSearches]
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map((search, index) => {
                    const title = search.original_query || search.query || 'Untitled Search';
                    const jobCount = search.total_jobs || 0;

                    // Parse timestamp with UTC handling
                    let dateString = search.created_at;
                    if (!dateString.includes('Z') && !dateString.includes('+')) {
                      dateString = dateString + 'Z'; // Force UTC parsing if no timezone
                    }
                    const date = new Date(dateString);

                    const isToday = date.toDateString() === new Date().toDateString();
                    const isYesterday = new Date(date.getTime() + 86400000).toDateString() === new Date().toDateString();

                    let dateDisplay = '';
                    if (isToday) {
                      dateDisplay = `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    } else if (isYesterday) {
                      dateDisplay = `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    } else {
                      dateDisplay = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
                    }

                    const isSelected = selectedSearchId === search.user_search_query_id;

                    return (
                      <div
                        key={`history-${search.user_search_query_id}-${index}`}
                        onClick={() => {
                          handleSearchChange(search.user_search_query_id);
                          setShowSearchHistoryModal(false);
                        }}
                        className="modern-card"
                        style={{
                          backgroundColor: isSelected ? 'var(--ion-color-light-tint)' : '#ffffff',
                          borderColor: isSelected ? 'var(--ion-color-primary)' : 'transparent',
                          padding: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px'
                        }}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '12px',
                          background: isSelected ? 'var(--ion-color-primary)' : 'var(--ion-color-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <IonIcon
                            icon={searchOutline}
                            style={{
                              fontSize: '20px',
                              color: isSelected ? '#ffffff' : 'var(--ion-color-medium)'
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{
                            margin: '0 0 4px 0',
                            fontSize: '15px',
                            fontWeight: '600',
                            color: isSelected ? 'var(--ion-color-primary)' : 'var(--ion-text-color)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {title}
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--ion-color-medium)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <IonIcon icon={briefcaseOutline} style={{ fontSize: '14px' }} />
                              {jobCount} jobs
                            </span>
                            <span style={{ fontSize: '13px', color: 'var(--ion-color-medium-tint)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <IonIcon icon={calendarOutline} style={{ fontSize: '14px' }} />
                              {dateDisplay}
                            </span>
                          </div>
                        </div>
                        {isSelected && (
                          <IonIcon icon={checkmarkCircleOutline} style={{ color: 'var(--ion-color-primary)', fontSize: '24px' }} />
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </IonContent>
      </IonModal>

      {/* Fit Analysis Modal */}
      <FitAnalysisModal
        isOpen={showFitAnalysisModal}
        onClose={() => {
          setShowFitAnalysisModal(false);
          setModalJobId(null);
          setModalJobTitle(undefined);
        }}
        jobId={modalJobId}
        jobTitle={modalJobTitle}
        userSearchId={selectedSearchId}
      />

      {/* Cover Letter Modal */}
      {showCoverLetterModal && (
        <CoverLetterModal
          isOpen={showCoverLetterModal}
          onClose={() => {
            setShowCoverLetterModal(false);
            setModalJobId(null);
            setModalJobTitle(undefined);
          }}
          jobId={modalJobId}
          jobTitle={modalJobTitle}
          userSearchId={selectedSearchId}
        />
      )}
    </IonPage>
  );
};

export default Jobs;